import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL, resolveMediaUrl } from './api';

const STATUS_RANK = { awaiting_payment: 0, dispatching: 1, pending: 1, pickup: 2, pickup_complete: 3, dropoff: 3, delivered: 4 };
const STEPS = [
  ['pending', 'Commande confirmée', 'Le restaurant prépare votre commande.'],
  ['pickup', 'Coursier en route', 'Un coursier vient récupérer votre commande.'],
  ['dropoff', 'Livraison en cours', 'Votre commande est en route vers vous.'],
  ['delivered', 'Commande livrée', 'Bonne dégustation !'],
];

function formatTime(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function formatCurrency(value) {
  return `${Number(value || 0).toFixed(2)} €`;
}

function statusCopy(status, imminent) {
  if (imminent && ['pickup', 'pickup_complete', 'dropoff'].includes(String(status || '').toLowerCase())) return 'Votre coursier arrive dans quelques instants';
  const labels = {
    awaiting_payment: 'Confirmation du paiement…', dispatching: 'Recherche d’un coursier…', pending: 'Recherche d’un coursier…',
    pickup: 'Le coursier se dirige vers le restaurant', pickup_complete: 'La commande vient d’être récupérée',
    dropoff: 'Votre commande est en route', delivered: 'Votre commande a été livrée',
    returned: 'La livraison a été retournée au restaurant', canceled: 'La livraison a été annulée', cancelled: 'La livraison a été annulée', dispatch_failed: 'Le restaurant organise votre livraison',
  };
  return labels[String(status || '').toLowerCase()] || 'Suivi de la livraison en cours';
}

function vehicleLabel(value) {
  return ({ bicycle: 'Vélo', car: 'Voiture', van: 'Utilitaire', truck: 'Camion', scooter: 'Scooter', motorcycle: 'Moto', walker: 'À pied' })[value] || value || 'Coursier';
}

function buildMapUrl(latitude, longitude) {
  if (latitude === null || latitude === undefined || latitude === '' || longitude === null || longitude === undefined || longitude === '') return '';
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return '';
  const spread = 0.012;
  const bbox = [lng - spread, lat - spread, lng + spread, lat + spread].join(',');
  return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${encodeURIComponent(`${lat},${lng}`)}`;
}

export default function ClickCollectTrackingView() {
  const { slug, token } = useParams();
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    let timer = null;
    const controller = new AbortController();
    setPayload(null);
    setError('');
    const load = async () => {
      try {
        const response = await fetch(`${API_URL}/api/click-collect/${encodeURIComponent(slug)}/tracking/${encodeURIComponent(token)}`, { cache: 'no-store', signal: controller.signal });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.error || 'Suivi indisponible.');
        if (active) { setPayload(data); setError(''); }
      } catch (err) {
        if (active) setError(err.message || 'Suivi indisponible.');
      } finally {
        if (active) timer = window.setTimeout(load, 10000);
      }
    };
    load();
    return () => { active = false; controller.abort(); if (timer) window.clearTimeout(timer); };
  }, [slug, token]);

  const delivery = payload?.delivery || {};
  const order = payload?.order || {};
  const restaurant = payload?.restaurant || {};
  const deliveryStatus = String(delivery.status || '').toLowerCase();
  const deliveryStopped = ['canceled', 'cancelled', 'returned'].includes(deliveryStatus);
  const showCourier = !deliveryStopped && deliveryStatus !== 'delivered';
  const currentRank = deliveryStopped ? 0 : STATUS_RANK[deliveryStatus] ?? 0;
  const mapUrl = useMemo(() => buildMapUrl(delivery?.courier?.latitude, delivery?.courier?.longitude), [delivery?.courier?.latitude, delivery?.courier?.longitude]);

  if (!payload && error) return <main className="cc-track-page"><section className="cc-track-card"><h1>Suivi indisponible</h1><p>{error}</p></section></main>;
  if (!payload) return <main className="cc-track-page"><section className="cc-track-card"><h1>Chargement du suivi…</h1></section></main>;

  return (
    <main className="cc-track-page" style={{ '--track-primary': restaurant.primaryColor, '--track-accent': restaurant.accentColor, '--track-bg': restaurant.backgroundColor }}>
      <header className="cc-track-header">
        {restaurant.logo ? <img src={resolveMediaUrl(restaurant.logo)} alt="" /> : <span className="cc-track-logo">{String(restaurant.name || 'B').slice(0, 1)}</span>}
        <div><small>Commande #{order.ticketNumber}</small><strong>{restaurant.name}</strong></div>
      </header>

      <section className="cc-track-hero">
        <span className="cc-track-live"><i /> {error ? "Actualisation interrompue" : "Suivi de commande"}</span>
        {error ? <p role="alert">{error} Les informations affichées peuvent être anciennes.</p> : null}
        <h1>{statusCopy(delivery.status, delivery.courierImminent)}</h1>
        {delivery.dropoffEta && !deliveryStopped && deliveryStatus !== 'delivered' ? <p>Arrivée estimée à <strong>{formatTime(delivery.dropoffEta)}</strong></p> : <p>Nous actualisons votre suivi automatiquement.</p>}
      </section>

      {mapUrl && showCourier ? (
        <section className="cc-track-map">
          <iframe key={mapUrl} title="Position du coursier" src={mapUrl} loading="eager" referrerPolicy="no-referrer" />
          <span>Position actualisée {formatTime(delivery.lastUpdateAt)}</span>
        </section>
      ) : null}

      {delivery.courier && showCourier ? (
        <section className="cc-track-card cc-track-courier">
          {delivery.courier.imageUrl ? <img src={delivery.courier.imageUrl} alt="" referrerPolicy="no-referrer" /> : <span className="cc-track-avatar">{String(delivery.courier.name || 'C').slice(0, 1)}</span>}
          <div><small>Votre coursier</small><strong>{delivery.courier.name || 'Coursier assigné'}</strong><span>{vehicleLabel(delivery.courier.vehicleType)}</span></div>
          {delivery.courier.phone ? <a href={`tel:${delivery.courier.phone}`}>Appeler{delivery.courier.phonePin ? ` · code ${delivery.courier.phonePin}` : ''}</a> : null}
        </section>
      ) : null}

      <section className="cc-track-card cc-track-timeline">
        {STEPS.map(([key, title, description], index) => {
          const rank = index + 1;
          const active = currentRank >= rank;
          const current = currentRank === rank && !deliveryStopped;
          return <div key={key} className={`${active ? 'is-done' : ''} ${current ? 'is-current' : ''}`}><i>{active ? '✓' : ''}</i><span><strong>{title}</strong><small>{description}</small></span></div>;
        })}
      </section>

      <section className="cc-track-card cc-track-order">
        <h2>Détail de la commande</h2>
        {(order.items || []).map((item) => <div key={item.id}><span>{item.quantity} × {item.name}</span></div>)}
        <div><span>Livraison</span><strong>{formatCurrency(order.deliveryFee)}</strong></div>
        <div className="is-total"><span>{order.paymentStatus === 'paid' ? 'Total payé' : 'Total de la commande'}</span><strong>{formatCurrency(order.totalTtc)}</strong></div>
        <p>Livraison à {order.destination?.addressLine1}, {order.destination?.postalCode} {order.destination?.city}</p>
      </section>

      <footer className="cc-track-footer">Suivi sécurisé par BECLICK · Données cartographiques © OpenStreetMap</footer>
    </main>
  );
}
